import SpeakButton from "../EnglishTool/SpeakButton";
import { getReadingPool } from "../EnglishTool/pool/readingPool";
import { getSentencePool } from "../EnglishTool/pool/sentencePool";

export const chapterPages = {
    1: [
      {
        title: <h2>Advanced Noun Clauses & Nominalization</h2>,
        body: (
          <>
            <p>
              <b>Noun Clauses:</b> Complex sentences often use noun clauses to introduce ideas, arguments, or reported speech.
              <br />
              <b>Subject:</b> <i><b>That she had anticipated the challenge</b> was impressive.</i>
              <SpeakButton text="That she had anticipated the challenge was impressive." /><br />
              <b>Object:</b> <i>We discussed <b>whether the proposal could be improved</b>.</i>
              <SpeakButton text="We discussed whether the proposal could be improved." />
            </p>
            <p>
              <b>Try it!</b> Identify the noun clause:<br />
              <i>No one understood <b>how the system operated under pressure</b>.</i>
              <SpeakButton text="No one understood how the system operated under pressure." />
            </p>
            <p>
              <b>Nominalization:</b> Transforming verbs/adjectives into nouns is essential for concise academic writing.<br />
              <i>"The team collaborated efficiently." → "Their <b>collaboration</b> was efficient."</i>
              <SpeakButton text="Their collaboration was efficient." />
              <br />
              <b>Challenge:</b> Change this verb to a noun: "They investigated." → ?
            </p>
            <p>
              <b>Embedded Appositives:</b> Appositives clarify or expand on key nouns, often in academic writing.<br />
              <i>Her essay, <b>a comprehensive analysis</b>, received top marks.</i>
              <SpeakButton text="Her essay, a comprehensive analysis, received top marks." />
              <br />
              <b>Try it!</b> Spot the appositive: "Professor Lee, <b>a renowned historian</b>, presented new findings."
            </p>
            <p>
              <b>Mini Reading:</b>
              <br />
              <i>{getReadingPool(9)[0]?.statement}</i>
              <br />
              <b>What is the main idea?</b>
            </p>
          </>
        ),
      },
      {
        title: <h2>Theme, Motif & Extended Metaphor in Academic Texts</h2>,
        body: (
          <>
            <p>
              <b>Theme:</b> The underlying insight or argument in a literary or informational work.
              <br />
              <b>Motif:</b> A recurring symbol or idea that reinforces the theme.
              <br />
              <b>Example:</b> <i>Isolation is a motif used to explore the theme of identity in the novel.</i>
              <SpeakButton text="Isolation is a motif used to explore the theme of identity in the novel." />
            </p>
            <p>
              <b>Extended Metaphor:</b> Used frequently in speeches, essays, and poetry to connect abstract ideas.
              <br />
              <b>Example:</b> <i>The author's journey through adversity is described as navigating an uncharted ocean.</i>
              <SpeakButton text="The author's journey through adversity is described as navigating an uncharted ocean." />
            </p>
            <p>
              <b>Mini Analysis:</b> Identify the motif in: <br />
              <i>"Every chapter begins with a closed door, symbolizing the protagonist’s obstacles."</i>
              <SpeakButton text="Every chapter begins with a closed door, symbolizing the protagonist’s obstacles." />
            </p>
            <p>
              <b>Challenge:</b> What is the extended metaphor in this passage?
              <br />
              <i>
                "Innovation, a fragile flame, must be protected from the winds of doubt."
                <SpeakButton text="Innovation, a fragile flame, must be protected from the winds of doubt." />
              </i>
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Complex Academic Sentences</h2>,
        body: (
          <>
            <p>
              <b>Find the noun clause, nominalization, and appositive:</b>
              <br />
              "That the hypothesis was confirmed, a surprising outcome, advanced the researcher’s reputation."
              <SpeakButton text="That the hypothesis was confirmed, a surprising outcome, advanced the researcher’s reputation." />
            </p>
            <p>
              <b>Try it!</b> Write a sentence using a noun clause and a sophisticated appositive.
            </p>
            <p>
              <b>Challenge:</b> Write an extended metaphor about learning, using a noun clause.
              <br />
              <i>Example: "How we process information becomes the blueprint for intellectual growth."</i>
              <SpeakButton text="How we process information becomes the blueprint for intellectual growth." />
            </p>
            <p>
              <b>Mini Reading:</b> {getReadingPool(9)[1]?.statement}
              <br />
              <b>What is the noun clause in: "Whatever the results indicate will shape future studies."</b>
              <SpeakButton text="Whatever the results indicate will shape future studies." />
            </p>
          </>
        ),
      },
    ],
  
    2: [
      {
        title: <h2>Verb Voice, Mood, & Precision in Academic Writing</h2>,
        body: (
          <>
            <p>
              <b>Active Voice:</b> Preferred in academic writing for clarity and strength.<br />
              <i>The researchers conducted a comprehensive survey.</i>
              <SpeakButton text="The researchers conducted a comprehensive survey." /><br />
              <b>Passive Voice:</b> Used for objectivity or when the doer is unknown.<br />
              <i>A comprehensive survey was conducted by the researchers.</i>
              <SpeakButton text="A comprehensive survey was conducted by the researchers." />
            </p>
            <p>
              <b>Try it!</b> Convert to passive: "The committee reached a unanimous decision."
            </p>
            <p>
              <b>Mood:</b>
              <ul>
                <li><b>Indicative:</b> States fact ("The author argues a point.")</li>
                <li><b>Imperative:</b> Command ("State your thesis clearly.")</li>
                <li><b>Interrogative:</b> Question ("Did you analyze all the data?")</li>
                <li><b>Conditional:</b> If/then ("If the sample size is large, the results are more reliable.")</li>
                <li><b>Subjunctive:</b> Hypothetical ("If I were to revise this essay, I would add more evidence.")</li>
              </ul>
              <SpeakButton text="If I were to revise this essay, I would add more evidence." />
            </p>
            <p>
              <b>Mini Reading:</b> {getReadingPool(9)[2]?.statement}
              <br />
              <b>What verb(s) express the main academic action?</b>
            </p>
          </>
        ),
      },
      {
        title: <h2>Sentence Structure, Fragments & Academic Run-Ons</h2>,
        body: (
          <>
            <p>
              <b>Fragments:</b> Common in drafts, but should be edited for completeness.<br />
              <i>"Because the results were inconclusive."</i>
              <SpeakButton text="Because the results were inconclusive." />
            </p>
            <p>
              <b>Run-On Sentences:</b> Weaken arguments; separate ideas with punctuation or conjunctions.<br />
              <i>"She completed her research she did not submit her findings."</i>
              <SpeakButton text="She completed her research she did not submit her findings." />
            </p>
            <p>
              <b>Try it!</b> Correct the run-on: "He analyzed the data he wrote the report."
            </p>
            <p>
              <b>Practice:</b> Write a run-on about a scientific process, then correct it.
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Advanced Verbs & Sentence Mastery</h2>,
        body: (
          <>
            <p>
              <b>Identify the verbs:</b><br />
              <i>{getSentencePool(9).find(s => s.focus === "verbs")?.sentence}</i>
            </p>
            <p>
              <b>Convert to passive:</b><br />
              <i>"The scientist observed a rare phenomenon."</i>
              <SpeakButton text="The scientist observed a rare phenomenon." />
            </p>
            <p>
              <b>Challenge:</b> Write a complex sentence with at least two clauses using advanced verbs.
            </p>
            <p>
              <b>Mini Reading:</b> {getReadingPool(9)[3]?.statement}
              <br />
              <b>Which verb best demonstrates analytical action?</b>
            </p>
          </>
        ),
      },
    ],
  
    3: [
      {
        title: <h2>Precise Adjectives & Vivid Academic Imagery</h2>,
        body: (
          <>
            <p>
              <b>Nuanced Adjectives:</b> Use discipline-specific adjectives for clarity.<br />
              <i>The <b>rigorous</b> methodology yielded <b>reliable</b> results.</i>
              <SpeakButton text="The rigorous methodology yielded reliable results." />
            </p>
            <p>
              <b>Imagery:</b> Strong adjectives create memorable academic arguments.<br />
              <i>A <b>stark</b> contrast emerged between the two groups.</i>
              <SpeakButton text="A stark contrast emerged between the two groups." />
            </p>
            <p>
              <b>Try it!</b> Describe a research process using two advanced adjectives.
            </p>
            <p>
              <b>Mini Analysis:</b> What adjectives add precision here:<br />
              <i>{getSentencePool(9).find(s => s.focus === "adjectives")?.sentence}</i>
            </p>
          </>
        ),
      },
      {
        title: <h2>Comparative, Superlative & Figurative Language in Analysis</h2>,
        body: (
          <>
            <p>
              <b>Comparative & Superlative:</b> Use to strengthen arguments in essays or reports.<br />
              <i>This approach is more effective than previous methods and the most comprehensive so far.</i>
              <SpeakButton text="This approach is more effective than previous methods and the most comprehensive so far." />
            </p>
            <p>
              <b>Figurative Adjectives:</b> For memorable arguments or literary analysis.<br />
              <i>The <b>volatile</b> market displayed <b>turbulent</b> shifts.</i>
              <SpeakButton text="The volatile market displayed turbulent shifts." />
            </p>
            <p>
              <b>Try it!</b> Write a comparative sentence about two theories.
            </p>
            <p>
              <b>Mini Reading:</b> {getReadingPool(9)[4]?.statement}
              <br />
              <b>Which adjective adds the strongest emphasis?</b>
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Adjectives & Analytical Imagery</h2>,
        body: (
          <>
            <p>
              <b>Write a short paragraph describing a process or discovery, using at least three academic adjectives and a metaphor.</b>
            </p>
            <p>
              <b>Find the adjectives:</b><br />
              <i>{getSentencePool(9).filter(s => s.focus === "adjectives")[1]?.sentence}</i>
            </p>
            <p>
              <b>Challenge:</b> Identify the imagery and figurative adjective in:<br />
              <b>"The silver data stream illuminated the researchers’ path through uncertainty."</b>
              <SpeakButton text="The silver data stream illuminated the researchers’ path through uncertainty." />
            </p>
          </>
        ),
      },
    ],
  
    4: [
      {
        title: <h2>Adverbs, Transitions & Rhetorical Analysis</h2>,
        body: (
          <>
            <p>
              <b>Adverbial Phrases:</b> Advanced writers use complex adverbials to clarify logic.<br />
              <i>The results emerged <b>with surprising consistency</b>.</i>
              <SpeakButton text="The results emerged with surprising consistency." />
            </p>
            <p>
              <b>Try it!</b> Compose a sentence about a scientific experiment using an advanced adverbial phrase.
            </p>
            <p>
              <b>Transitions & Conjunctive Adverbs:</b> Critical for sophisticated essays.<br />
              <i>"Furthermore, the hypothesis was supported by the evidence." / "The model failed; <b>consequently</b>, revisions were necessary."</i>
              <SpeakButton text="The model failed; consequently, revisions were necessary." />
            </p>
            <p>
              <b>Media & Rhetoric:</b> Identify how adverbs shape persuasive arguments in editorials, essays, or research papers.<br />
              <i>"Remarkably, the team resolved the problem overnight."</i>
              <SpeakButton text="Remarkably, the team resolved the problem overnight." />
            </p>
          </>
        ),
      },
      {
        title: <h2>Analyzing Rhetorical Devices & Adverb Mastery</h2>,
        body: (
          <>
            <p>
              <b>Rhetorical Adverbs:</b> Words like <b>inevitably, consequently, theoretically</b> can reinforce argumentation.<br />
              <i>"Inevitably, the theory was challenged by new data."</i>
              <SpeakButton text="Inevitably, the theory was challenged by new data." />
            </p>
            <p>
              <b>Find the adverb:</b><br />
              <i>{getSentencePool(9).find(s => s.focus === "adverbs")?.sentence}</i>
            </p>
            <p>
              <b>Mini Reading:</b> {getReadingPool(9)[2]?.statement}
              <br />
              <b>Which adverb strengthens the conclusion?</b>
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Adverbs, Transitions, & Persuasion</h2>,
        body: (
          <>
            <p>
              <b>Write a persuasive statement about an academic topic, using at least two advanced adverbs and a conjunctive adverb.</b>
            </p>
            <p>
              <b>Find the adverbs:</b><br />
              <i>{getSentencePool(9).filter(s => s.focus === "adverbs")[1]?.sentence}</i>
            </p>
            <p>
              <b>Challenge:</b> Identify the rhetorical strategy in:<br />
              <b>"Undoubtedly, collaboration accelerates scientific progress."</b>
              <SpeakButton text="Undoubtedly, collaboration accelerates scientific progress." />
            </p>
          </>
        ),
      },
    ]
  };